// A composição do DAS do MEI — a regra, num lugar só, pura, com prova própria
// (`app/tests/das-mei.mjs`).
//
// ---------------------------------------------------------------------------
// Por que ICMS e ISS saem do FATO da ocupação, e não do texto dela
// ---------------------------------------------------------------------------
// O simulador de origem decidia o tributo pelo PREFIXO da descrição do CNAE:
//
//     if (s.startsWith("comércio"))  return "Comercio";     // → ICMS
//     if (s.includes("indústria"))   return "Industria";    // → ICMS
//     return "Servicos";                                    // → ISS, todo o resto
//
// "Transporte rodoviário de carga" cai no `return` final, então o DAS do
// caminhoneiro intermunicipal saía com ISS de R$ 5,00 e ICMS zero. A lista de
// ocupações do hub diz o contrário, e diz por ocupação (`mei_ocupacoes.icms`,
// `.iss`, medidas no legado): 4930-2/02 é ICMS **sem** ISS; 4930-2/01 é ISS;
// 4930-2/03 e /04 são os dois. Nenhuma heurística de texto acerta isso, porque
// a informação não está no texto.
//
// O caso 11 do teste 46 existe para que ninguém volte a adivinhar por string o
// que está medido por ocupação.
//
// ---------------------------------------------------------------------------
// O que o "MEI Caminhoneiro" muda, e o que ele NÃO muda
// ---------------------------------------------------------------------------
// Muda o INSS: 12% do salário mínimo em vez de 5% (LC 188/2021), e o teto anual
// (R$ 251.600 em vez de R$ 81.000). **Não** muda ICMS nem ISS — esses continuam
// saindo da atividade escolhida. O simulador de origem cravava `temCI: true,
// temS: true` para o caminhoneiro, o que cobra os dois tributos de quem só deve
// um.
//
// ---------------------------------------------------------------------------
// Os valores vêm do BANCO
// ---------------------------------------------------------------------------
// Nada de número de tributo escrito aqui. `mei_das_parametros()` (000098)
// devolve o INSS já calculado E a base que o explica — salário e alíquota —, e
// este módulo **não multiplica de novo**: recebe `inss_geral` pronto e usa. Se
// recalculasse, passariam a existir duas contas do mesmo INSS, que é o defeito
// que a 000091 pagou com a explicação do teto.

export type ParametrosDas = {
  salario_minimo: number;
  aliquota_inss_geral: number;
  aliquota_inss_caminhoneiro: number;
  /** Já calculado no banco: `round(salário × alíquota, 2)`. Não recalcule. */
  inss_geral: number;
  inss_caminhoneiro: number;
  /** Fixos na LC 123/2006 art. 18-A — não derivam de base nenhuma. */
  icms: number;
  iss: number;
  teto: number;
  teto_caminhoneiro: number;
};

/** O que decide ICMS e ISS: o fato gravado na ocupação, não o nome dela. */
export type AtividadeEscolhida = {
  atividade: string;
  cnae: string | null;
  icms: boolean | null;
  iss: boolean | null;
};

export type Composicao = {
  inss: number;
  icms: number;
  iss: number;
  total: number;
  /** O porquê do INSS, saindo de onde o número saiu. */
  baseInss: { salario: number; aliquota: number; caminhoneiro: boolean };
  /** Quais atividades puxaram cada tributo — a tela mostra isto ao lado do valor. */
  porqueIcms: string[];
  porqueIss: string[];
  /**
   * Sem atividade escolhida não existe composição: o INSS todo MEI paga, mas
   * ICMS e ISS dependem do ramo. A tela pede a atividade em vez de chutar.
   *
   * O simulador de origem, neste caso, fazia `if (!temCI && !temS) temS = true`
   * — cobrava ISS de quem não escolheu nada. Chute de tributo é pior que campo
   * vazio: o aluno anota o número e leva para a prova.
   */
  semAtividade: boolean;
};

/** Soma em centavos: 81.05 + 1 + 5 em ponto flutuante não fecha redondo. */
function somar(...valores: number[]): number {
  return Math.round(valores.reduce((s, v) => s + Math.round(v * 100), 0)) / 100;
}

export function compor(
  p: ParametrosDas,
  atividades: AtividadeEscolhida[],
  caminhoneiro: boolean,
): Composicao {
  const inss = caminhoneiro ? p.inss_caminhoneiro : p.inss_geral;
  const aliquota = caminhoneiro ? p.aliquota_inss_caminhoneiro : p.aliquota_inss_geral;

  // `icms` e `iss` são `boolean | null` na lista: nulo é "a lista não diz", e
  // não "não paga" — são coisas diferentes, e é por isso que a comparação é
  // explícita com `=== true` em vez de truthiness. Um nulo virando "não paga"
  // aqui tiraria um tributo do DAS sem ninguém ter decidido isso.
  const porqueIcms = atividades.filter((a) => a.icms === true).map((a) => a.atividade);
  const porqueIss = atividades.filter((a) => a.iss === true).map((a) => a.atividade);

  const icms = porqueIcms.length > 0 ? p.icms : 0;
  const iss = porqueIss.length > 0 ? p.iss : 0;

  return {
    inss,
    icms,
    iss,
    total: somar(inss, icms, iss),
    baseInss: { salario: p.salario_minimo, aliquota, caminhoneiro },
    porqueIcms,
    porqueIss,
    semAtividade: atividades.length === 0,
  };
}

/**
 * O vencimento do DAS: dia 20 do mês SEGUINTE ao período de apuração.
 *
 * `mes` é 1..12. Dezembro vence em 20 de janeiro do ano seguinte, e é por isso
 * que a conta usa `new Date(ano, mes, 20)` — com `mes` já valendo "o mês
 * seguinte" na contagem do JavaScript, que é base zero. Escrever `mes + 1`
 * aqui seria o erro clássico de virada de ano.
 */
export function vencimentoDo(ano: number, mes: number): Date {
  return new Date(ano, mes, 20);
}

/**
 * O período é apurável? Não antes do mês de abertura do MEI — antes disso a
 * empresa não existia, e o PGMEI mostra "Indisponível".
 *
 * `mesAbertura` é 1..12.
 */
export function apuravel(
  ano: number,
  mes: number,
  aberturaAno: number,
  mesAbertura: number,
): boolean {
  if (ano < aberturaAno) return false;
  if (ano > aberturaAno) return true;
  return mes >= mesAbertura;
}

/**
 * O teto que vale para o ano, e o excesso.
 *
 * A regra do desenquadramento tem DUAS faixas, e a diferença entre elas é a
 * consequência: até 20% de excesso o MEI paga DAS complementar e migra para ME
 * no ano seguinte; acima de 20% o desenquadramento é RETROATIVO a janeiro. Uma
 * tela que só diga "passou do teto" esconde a diferença que importa.
 */
export type Excesso = {
  teto: number;
  total: number;
  percentual: number;
  faixa: "dentro" | "ate-20" | "acima-de-20";
};

export function excessoDoAno(
  p: ParametrosDas,
  total: number,
  caminhoneiro: boolean,
): Excesso {
  const teto = caminhoneiro ? p.teto_caminhoneiro : p.teto;
  // Guarda contra teto zero: divisão por zero daria Infinity e a tela
  // imprimiria "Infinity%". O banco recusa teto zero, mas a tela não é o lugar
  // de confiar nisso — é o lugar de não quebrar.
  const percentual = teto > 0 ? (total / teto) * 100 : 0;
  const faixa: Excesso["faixa"] =
    teto <= 0 || total <= teto ? "dentro" : total <= teto * 1.2 ? "ate-20" : "acima-de-20";
  return { teto, total, percentual, faixa };
}

// ---------------------------------------------------------------------------
// O calendário: quais anos se pode declarar, e quando cada coisa vence
// ---------------------------------------------------------------------------
// Isto é regra, não apresentação — e mora aqui, junto do resto da regra, para
// ter a mesma prova. As funções recebem `agora` em vez de chamarem `new Date()`
// justamente para poderem ser provadas com qualquer data.

export const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

/** `DD/MM/AAAA`. */
export function dataBr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** `DD/MM/AAAA HH:MM` — a estampa de emissão dos documentos. */
export function dataHoraBr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dataBr(d)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export type AnoDasn = {
  ano: number;
  situacao: "Declarar" | "Retificar" | "Não optante";
  bloqueado: boolean;
};

/**
 * Os anos-calendário que a DASN-SIMEI oferece, DERIVADOS do ano corrente.
 *
 * A DASN de um ano-calendário é entregue até 31 de maio do ano seguinte, então
 * o "Declarar" é sempre `ano corrente − 1`; os três anteriores são retificação,
 * e o quinto já saiu do alcance.
 *
 * O simulador de origem cravava a lista no código — `2025 Declarar`,
 * `2024…2022 Retificar`, `2021 Não Optante`. É a armadilha do "no ano de 2025"
 * nos enunciados do IR Proposta, de novo: em 2027 a ferramenta convidaria o
 * aluno a DECLARAR 2025, que já é retificação, e a tela ensinaria o prazo
 * errado a quem está aprendendo o prazo. Lista de anos nasce do calendário,
 * nunca do dia em que o arquivo foi escrito.
 */
export function anosDaDasn(agora: Date): AnoDasn[] {
  const declarar = agora.getFullYear() - 1;
  return [
    { ano: declarar, situacao: "Declarar", bloqueado: false },
    { ano: declarar - 1, situacao: "Retificar", bloqueado: false },
    { ano: declarar - 2, situacao: "Retificar", bloqueado: false },
    { ano: declarar - 3, situacao: "Retificar", bloqueado: false },
    { ano: declarar - 4, situacao: "Não optante", bloqueado: true },
  ];
}

/** Os anos que a apuração do DAS oferece: o corrente e os dois anteriores. */
export function anosDoDas(agora: Date): number[] {
  const a = agora.getFullYear();
  return [a - 2, a - 1, a];
}
